import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import ProjectStatusIcon from '@atlaskit/icon/core/project-status';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import AssetsDataManagerIcon from '@atlaskit/icon-lab/core/assets-data-manager';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import TextRephraseIcon from '@atlaskit/icon-lab/core/text-rephrase';

import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { triggerFileUploadDialog } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/file-upload-trigger/trigger-file-upload-dialog';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import CelebrationSpot from '../assets/celebration.svg';

import { useCreateSpaceStoreActions } from '@confluence/create-space/entry-points/createSpace.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import {
	useContentType as useContentTypeExternal,
	usePageSpaceKey,
} from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { IntentKeepMomentumIcon } from '../icons/IntentKeepMomentumIcon.tsx';
import { IntentSetUpSpaceIcon } from '../icons/IntentSetUpSpaceIcon.tsx';
import { IntentStartCreatingIcon } from '../icons/IntentStartCreatingIcon.tsx';
import { messages } from '../messages.ts';

import { useRecentJiraProjectAndIssue } from './useRecentJiraProjectAndIssue.tsx';

type Greeting = {
	title: string;
	subtitle: string;
	iconSrc?: string;
	titleSize?: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall';
};

export type OnboardingRovoConfig = {
	actions: ConversationAction[];
	greeting: Greeting;
};

type UseOnboardingRovoConfigParams = {
	/** When true, the hook short-circuits and returns null. */
	skip: boolean;
	/** Whether the user skipped AIFC during onboarding */
	skippedAifc: boolean;
};

export type OnboardingContentType = 'page' | 'whiteboard' | 'database' | 'liveDoc';

const ACTION_ID_SUFFIX = {
	landCompletedAifc: '-land-completed-aifc',
	landSkippedAifc: '-land-skipped-aifc',
	xflowCompletedAifc: '-xflow-completed-aifc',
	xflowSkippedAifc: '-xflow-skipped-aifc',
} as const;

const useContentType = (): OnboardingContentType => {
	const [contentType] = useContentTypeExternal();
	const isLivePage = useIsCurrentPageLive();
	if (contentType === 'whiteboard' || contentType === 'database') {
		return contentType;
	}
	return isLivePage ? 'liveDoc' : 'page';
};

const isEpic = (issueTypeHierarchyLevel: number | null): boolean => issueTypeHierarchyLevel === 1;

/**
 * Returns an `OnboardingRovoConfig` when the onboarding entry point is active,
 * or `null` when `skip` is true (i.e. no entry point was detected).
 */
export function useOnboardingRovoConfig({
	skip,
	skippedAifc,
}: UseOnboardingRovoConfigParams): OnboardingRovoConfig | null {
	const currentContentType = useContentType();
	const {
		data: { hasFullData: isXflowUser, issue, project },
		isLoading,
	} = useRecentJiraProjectAndIssue({ skip });
	const { formatMessage } = useIntl();

	// The greeting title is only enlarged in the mini-modal view. In larger views
	// (e.g. sidebar) we omit `titleSize` so the conversation-assistant falls back
	// to its default heading size.
	const { mode: rovoUiMode } = useRovoExperienceAPIStore();
	const isMiniModal = rovoUiMode === ConversationAssistantUIModes.MINI_MODAL;
	const miniModalTitleSize = useMemo(
		() => (isMiniModal ? ({ titleSize: 'large' } as const) : {}),
		[isMiniModal],
	);

	const { orgId, cloudId, environment } = useSessionData();

	const openSitePermissions = useCallback(() => {
		if (!orgId || !environment) {
			return;
		}
		const adminHubBase =
			environment === 'PRODUCTION'
				? 'https://admin.atlassian.com'
				: 'https://admin.stg.atlassian.com';
		window.open(`${adminHubBase}/o/${orgId}/users`, '_blank', 'noopener,noreferrer');
	}, [orgId, environment]);

	const openConnectYourTools = useCallback(() => {
		if (!orgId || !cloudId || !environment) {
			return;
		}
		const adminHubBase =
			environment === 'PRODUCTION'
				? 'https://admin.atlassian.com'
				: 'https://admin.stg.atlassian.com';
		window.open(
			`${adminHubBase}/o/${orgId}/connectors?site-id=${cloudId}`,
			'_blank',
			'noopener,noreferrer',
		);
	}, [orgId, cloudId, environment]);

	const { openModal: openCwrModal } = useSmartCreationModalActions();

	const { openDrawer: openCreateSpaceDrawer } = useCreateSpaceStoreActions();

	const [pageSpaceKey] = usePageSpaceKey();

	const openCwrCreateNew = useCallback(() => {
		openCwrModal({ entryPoint: 'rovo-onboarding', spaceKey: pageSpaceKey ?? '' });
	}, [openCwrModal, pageSpaceKey]);

	const openCreateSpace = useCallback(() => {
		openCreateSpaceDrawer({ current: null }, undefined);
	}, [openCreateSpaceDrawer]);

	const currentContentTypeLabel = useMemo(() => {
		switch (currentContentType) {
			case 'whiteboard':
				return formatMessage(messages.contentTypeWhiteboard);
			case 'database':
				return formatMessage(messages.contentTypeDatabase);
			case 'liveDoc':
			case 'page':
			default:
				return formatMessage(messages.contentTypeDoc);
		}
	}, [currentContentType, formatMessage]);

	const outputContentTypeLabel = useMemo(() => {
		switch (currentContentType) {
			case 'whiteboard':
			case 'database':
				return formatMessage(messages.contentTypeDoc);
			case 'liveDoc':
			case 'page':
			default:
				return formatMessage(messages.contentTypeWhiteboard);
		}
	}, [currentContentType, formatMessage]);

	const landSkippedAifcConfig = useMemo<OnboardingRovoConfig>(() => {
		const suffix = ACTION_ID_SUFFIX.landSkippedAifc;
		return {
			greeting: {
				title: formatMessage(messages.welcomeTitle),
				subtitle: formatMessage(messages.welcomeSubtitle),
				iconSrc: CelebrationSpot,
				...miniModalTitleSize,
			},
			actions: [
				{
					id: `rovo-onboarding-start-creating${suffix}`,
					title: formatMessage(messages.intentStartCreatingLabel),
					secondaryDescription: formatMessage(messages.intentStartCreatingSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentStartCreatingIcon,
					children: [
						{
							id: `rovo-onboarding-create-something-new${suffix}`,
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-create-from-file${suffix}`,
							title: formatMessage(messages.createFromFile),
							alwaysShowSecondaryText: true,
							icon: () => <AttachmentIcon label="" />,
							onClick: triggerFileUploadDialog,
							dynamicPrompt: formatMessage(messages.createDocFromFile),
							type: 'ai-action',
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-create-from-template${suffix}`,
							title: formatMessage(messages.createFromTemplate),
							alwaysShowSecondaryText: true,
							icon: () => <TemplateIcon label="" />,
							onClick: () => window.open('/wiki/templates', '_blank', 'noopener,noreferrer'),

							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: `rovo-onboarding-set-up-space${suffix}`,
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentSetUpSpaceIcon,
					children: [
						{
							id: `rovo-onboarding-create-space${suffix}`,
							title: formatMessage(messages.createSpace),
							alwaysShowSecondaryText: true,
							icon: () => <GridIcon label="" />,
							onClick: openCreateSpace,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-set-up-site-permissions${suffix}`,
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: openSitePermissions,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-connect-your-tools${suffix}`,
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: openConnectYourTools,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		};
	}, [
		formatMessage,
		openCwrCreateNew,
		openCreateSpace,
		openSitePermissions,
		openConnectYourTools,
		miniModalTitleSize,
	]);

	const landCompletedAifcConfig = useMemo<OnboardingRovoConfig>(() => {
		const suffix = ACTION_ID_SUFFIX.landCompletedAifc;
		return {
			greeting: {
				title: formatMessage(messages.nicelyDoneTitle),
				subtitle: formatMessage(messages.nicelyDoneSubtitle),
				iconSrc: CelebrationSpot,
				...miniModalTitleSize,
			},
			actions: [
				{
					id: `rovo-onboarding-keep-momentum${suffix}`,
					title: formatMessage(messages.intentKeepMomentumLabel),
					secondaryDescription: formatMessage(messages.intentKeepMomentumSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentKeepMomentumIcon,
					children: [
						{
							id: `rovo-onboarding-turn-content-into-content${suffix}`,
							title: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ShapesIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-turn-into-action-items${suffix}`,
							title: formatMessage(messages.turnContentTypeIntoActionItems, {
								contentType: currentContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <AiGenerativeTextSummaryIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoActionItems, {
								contentType: currentContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-create-something-new${suffix}`,
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: `rovo-onboarding-set-up-space${suffix}`,
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentSetUpSpaceIcon,
					children: [
						{
							id: `rovo-onboarding-create-space${suffix}`,
							title: formatMessage(messages.createSpace),
							alwaysShowSecondaryText: true,
							icon: () => <GridIcon label="" />,
							onClick: openCreateSpace,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-set-up-site-permissions${suffix}`,
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: openSitePermissions,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-connect-your-tools${suffix}`,
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: openConnectYourTools,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		};
	}, [
		currentContentTypeLabel,
		formatMessage,
		openCreateSpace,
		openCwrCreateNew,
		openSitePermissions,
		openConnectYourTools,
		outputContentTypeLabel,
		miniModalTitleSize,
	]);

	const xflowCompletedAifcConfig = useMemo<OnboardingRovoConfig>(() => {
		const suffix = ACTION_ID_SUFFIX.xflowCompletedAifc;
		return {
			greeting: {
				title: formatMessage(messages.nicelyDoneTitle),
				subtitle: formatMessage(messages.nicelyDoneSubtitle),
				iconSrc: CelebrationSpot,
				...miniModalTitleSize,
			},
			actions: [
				{
					id: `rovo-onboarding-keep-momentum${suffix}`,
					title: formatMessage(messages.intentKeepMomentumLabel),
					secondaryDescription: formatMessage(messages.intentKeepMomentumSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentKeepMomentumIcon,
					children: [
						{
							id: `rovo-onboarding-turn-content-into-content${suffix}`,
							title: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ShapesIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: isEpic(issue.issueTypeHierarchyLevel)
								? `rovo-onboarding-create-scope-doc${suffix}`
								: `rovo-onboarding-break-down-issue${suffix}`,
							title: formatMessage(
								isEpic(issue.issueTypeHierarchyLevel)
									? messages.createScopeDocLink
									: messages.breakDownIssueLink,
								{
									issueLink: issue.webUrl,
								},
							),
							alwaysShowSecondaryText: true,
							icon: () => <TextRephraseIcon label="" />,
							staticPrompt: formatMessage(
								isEpic(issue.issueTypeHierarchyLevel)
									? messages.createScopeDocLink
									: messages.breakDownIssueLink,
								{
									issueLink: issue.webUrl,
								},
							),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-create-something-new${suffix}`,
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: `rovo-onboarding-set-up-space${suffix}`,
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentSetUpSpaceIcon,
					children: [
						{
							id: `rovo-onboarding-build-knowledge-hub${suffix}`,
							title: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.webUrl,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <DatabaseIcon label="" />,
							staticPrompt: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.webUrl,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-set-up-site-permissions${suffix}`,
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: openSitePermissions,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-connect-your-tools${suffix}`,
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: openConnectYourTools,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		};
	}, [
		currentContentTypeLabel,
		formatMessage,
		issue.issueTypeHierarchyLevel,
		issue.webUrl,
		openCwrCreateNew,
		openSitePermissions,
		openConnectYourTools,
		outputContentTypeLabel,
		miniModalTitleSize,
		project.webUrl,
	]);

	const xflowSkippedAifcConfig = useMemo<OnboardingRovoConfig>(() => {
		const suffix = ACTION_ID_SUFFIX.xflowSkippedAifc;
		return {
			greeting: {
				title: formatMessage(messages.welcomeTitle),
				subtitle: formatMessage(messages.welcomeSubtitle),
				iconSrc: CelebrationSpot,
				...miniModalTitleSize,
			},
			actions: [
				{
					id: `rovo-onboarding-start-creating${suffix}`,
					title: formatMessage(messages.intentStartCreatingLabel),
					secondaryDescription: formatMessage(messages.intentStartCreatingSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentStartCreatingIcon,
					children: [
						{
							id: `rovo-onboarding-write-status-update${suffix}`,
							title: formatMessage(messages.writeStatusUpdate, {
								projectName: project.webUrl,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ProjectStatusIcon label="" />,
							staticPrompt: formatMessage(messages.writeStatusUpdate, {
								projectName: project.webUrl,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-break-down-issue${suffix}`,
							title: formatMessage(messages.breakDownIssueLink, {
								issueLink: issue.webUrl,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <TextRephraseIcon label="" />,
							staticPrompt: formatMessage(messages.breakDownIssueLink, {
								issueLink: issue.webUrl,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-create-something-new${suffix}`,
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: `rovo-onboarding-set-up-space${suffix}`,
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: IntentSetUpSpaceIcon,
					children: [
						{
							id: `rovo-onboarding-build-knowledge-hub${suffix}`,
							title: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.webUrl,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <DatabaseIcon label="" />,
							staticPrompt: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.webUrl,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: `rovo-onboarding-set-up-site-permissions${suffix}`,
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: openSitePermissions,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: `rovo-onboarding-connect-your-tools${suffix}`,
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: openConnectYourTools,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		};
	}, [
		formatMessage,
		issue.webUrl,
		openCwrCreateNew,
		openSitePermissions,
		openConnectYourTools,
		project.webUrl,
		miniModalTitleSize,
	]);

	if (skip || isLoading) {
		return null;
	}

	if (isXflowUser) {
		return skippedAifc ? xflowSkippedAifcConfig : xflowCompletedAifcConfig;
	}

	return skippedAifc ? landSkippedAifcConfig : landCompletedAifcConfig;
}
